import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { XbotCoverOverlay } from '../components/XbotCoverOverlay';
import { toast } from 'sonner';
import {
  Image, Video, Play, Upload, ChevronLeft, Filter,
  Film, Camera, Trophy, Clock, MapPin, X, Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  highlight: { label: 'Highlight', icon: Film, cls: 'text-amber-400 border-amber-800 bg-amber-500/10' },
  full_game: { label: 'Full Game', icon: Video, cls: 'text-cyan-400 border-cyan-800 bg-cyan-500/10' },
  photo: { label: 'Photo', icon: Camera, cls: 'text-emerald-400 border-emerald-800 bg-emerald-500/10' },
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ProgramMedia = () => {
  const { slug } = useParams();
  const { token, user } = useAuth();
  const [program, setProgram] = useState(null);
  const [media, setMedia] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterTeam, setFilterTeam] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [teams, setTeams] = useState([]);
  const [events, setEvents] = useState([]);
  const [myRole, setMyRole] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const isCoachOrAdmin = myRole && ['program_admin', 'super_admin', 'coach'].includes(myRole);

  const color = program?.primary_color || '#22c55e';
  const colorBg = `${color}15`;

  const loadMedia = useCallback(async (prog) => {
    if (!prog) return;
    const params = new URLSearchParams();
    if (filterTeam) params.set('team_id', filterTeam);
    if (filterCategory) params.set('category', filterCategory);
    params.set('limit', '60');
    const res = await fetch(`${API_URL}/api/programs/${prog.id}/media-feed?${params}`);
    if (res.ok) {
      const data = await res.json();
      setMedia(data.media || []);
      setTotal(data.total || 0);
    }
  }, [filterTeam, filterCategory]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const progRes = await fetch(`${API_URL}/api/programs/by-slug/${slug}`);
        if (!progRes.ok) { setLoading(false); return; }
        const prog = await progRes.json();
        setProgram(prog);

        const [teamRes] = await Promise.all([
          fetch(`${API_URL}/api/programs/${prog.id}/teams`),
        ]);
        if (teamRes.ok) setTeams(await teamRes.json());

        await loadMedia(prog);

        if (token) {
          try {
            const myProgs = await fetch(`${API_URL}/api/programs/my-programs`, { headers }).then(r => r.json());
            const mine = myProgs.find(p => p.id === prog.id);
            setMyRole(mine?.my_role || (['admin', 'super_admin'].includes(user?.role) ? 'super_admin' : null));
          } catch {}
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [slug, token]);

  useEffect(() => {
    if (program) loadMedia(program);
  }, [filterTeam, filterCategory, program, loadMedia]);

  // Load events when program loads (for upload form)
  useEffect(() => {
    if (!program) return;
    fetch(`${API_URL}/api/programs/${program.id}/schedule?limit=30`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setEvents(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [program]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
    </div>
  );

  if (!program) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-zinc-500">Program not found</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-black" data-testid="program-media-page">
      {/* Header */}
      <div className="border-b border-zinc-800/60 bg-zinc-950/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to={`/${slug}/dashboard`}
                className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs transition-colors"
                data-testid="back-to-dashboard"
              >
                <ChevronLeft className="w-4 h-4" /> Dashboard
              </Link>
              <div className="w-px h-4 bg-zinc-800" />
              <h1 className="text-white font-bold text-base sm:text-lg">Media</h1>
              <span className="text-zinc-600 text-xs">{total} items</span>
            </div>
            {isCoachOrAdmin && (
              <Button
                onClick={() => setShowUpload(true)}
                className="text-xs gap-1.5"
                style={{ backgroundColor: color }}
                data-testid="upload-media-btn"
              >
                <Upload className="w-3.5 h-3.5" /> Upload
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="media-filters">
            <Filter className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
            <select
              value={filterTeam}
              onChange={e => setFilterTeam(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-lg px-2.5 py-1.5 focus:border-zinc-600 outline-none"
              data-testid="filter-team"
            >
              <option value="">All Teams</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-lg px-2.5 py-1.5 focus:border-zinc-600 outline-none"
              data-testid="filter-category"
            >
              <option value="">All Types</option>
              <option value="highlight">Highlights</option>
              <option value="full_game">Full Games</option>
              <option value="photo">Photos</option>
            </select>
            {(filterTeam || filterCategory) && (
              <button onClick={() => { setFilterTeam(''); setFilterCategory(''); }} className="text-zinc-500 text-[10px] hover:text-white flex items-center gap-0.5">
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Media Grid */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {media.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-12 h-12 text-zinc-800 mx-auto mb-3" />
            <p className="text-zinc-600 text-sm">No media uploaded yet</p>
            {isCoachOrAdmin && (
              <Button onClick={() => setShowUpload(true)} variant="outline" className="mt-4 text-xs border-zinc-700 text-zinc-400">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload First Media
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="media-grid">
            {media.map(m => (
              <MediaCard key={m.id} item={m} color={color} onClick={() => setSelectedMedia(m)} />
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <UploadModal
          program={program}
          teams={teams}
          events={events}
          token={token}
          color={color}
          onClose={() => setShowUpload(false)}
          onUploaded={(newMedia) => {
            setMedia(prev => [newMedia, ...prev]);
            setTotal(prev => prev + 1);
            setShowUpload(false);
          }}
        />
      )}

      {/* Media Lightbox */}
      {selectedMedia && (
        <Dialog open onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl p-0 overflow-hidden">
            <div className="relative">
              {selectedMedia.media_type === 'video' ? (
                <>
                  <video src={`${API_URL}${selectedMedia.url}`} controls className="w-full max-h-[70vh]" autoPlay />
                  <XbotCoverOverlay />
                </>
              ) : (
                <img src={`${API_URL}${selectedMedia.url}`} alt={selectedMedia.title || ''} className="w-full max-h-[70vh] object-contain bg-black" />
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                {selectedMedia.category && CATEGORY_META[selectedMedia.category] && (
                  <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${CATEGORY_META[selectedMedia.category].cls}`}>
                    {CATEGORY_META[selectedMedia.category].label}
                  </Badge>
                )}
                {selectedMedia.team_name && (
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-zinc-400 border-zinc-700">{selectedMedia.team_name}</Badge>
                )}
              </div>
              <h3 className="text-white font-semibold text-sm">{selectedMedia.title || 'Untitled'}</h3>
              <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-1">
                {selectedMedia.event_name && <span className="flex items-center gap-1"><Trophy className="w-3 h-3" /> {selectedMedia.event_name}</span>}
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(selectedMedia.created_at)}</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};


/* ========== MEDIA CARD COMPONENT ========== */
const MediaCard = ({ item, color, onClick }) => {
  const cat = CATEGORY_META[item.category] || CATEGORY_META.photo;
  const CatIcon = cat.icon;
  const isVideo = item.media_type === 'video';
  const thumbUrl = item.thumbnail_url
    ? (item.thumbnail_url.startsWith('http') ? item.thumbnail_url : `${API_URL}${item.thumbnail_url}`)
    : null;
  const mediaUrl = item.url?.startsWith('http') ? item.url : `${API_URL}${item.url}`;

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer rounded-xl overflow-hidden bg-zinc-900/80 border border-zinc-800/60 hover:border-zinc-700 transition-all"
      data-testid={`media-card-${item.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-zinc-800 overflow-hidden">
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        ) : !isVideo ? (
          <img src={mediaUrl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/10 group-hover:bg-white/20 transition-colors flex items-center justify-center">
              <Play className="w-4 h-4 text-white ml-0.5" />
            </div>
          </div>
        )}

        {/* Category badge */}
        <Badge className={`absolute top-2 left-2 text-[8px] px-1.5 py-0 border backdrop-blur-sm ${cat.cls}`}>
          <CatIcon className="w-2.5 h-2.5 mr-0.5" /> {cat.label}
        </Badge>

        {/* Video play overlay */}
        {isVideo && thumbUrl && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Play className="w-4 h-4 text-white ml-0.5" />
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5">
        <h3 className="text-white text-xs font-medium line-clamp-1">{item.title || 'Untitled'}</h3>
        <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
          {item.team_name && <span className="truncate max-w-[100px]">{item.team_name}</span>}
          {item.team_name && item.event_name && <span className="text-zinc-700">·</span>}
          {item.event_name && <span className="truncate max-w-[100px]">{item.event_name}</span>}
        </div>
        <span className="text-[10px] text-zinc-600 block mt-0.5">{formatDate(item.created_at)}</span>
      </div>
    </div>
  );
};


/* ========== UPLOAD MODAL ========== */
const UploadModal = ({ program, teams, events, token, color, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [teamId, setTeamId] = useState('');
  const [eventId, setEventId] = useState('');
  const [category, setCategory] = useState('photo');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setCategory(f.type.startsWith('video/') ? 'highlight' : 'photo');
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(10);
    try {
      // Step 1: Upload file
      const formData = new FormData();
      formData.append('file', file);
      setProgress(25);

      const uploadRes = await fetch(`${API_URL}/api/uploads/program-media/${program.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const uploadData = await uploadRes.json();
      setProgress(70);

      // Step 2: Create media record
      const mediaRes = await fetch(`${API_URL}/api/programs/${program.id}/media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: title || file.name.replace(/\.[^/.]+$/, ''),
          team_id: teamId || null,
          event_id: eventId || null,
          media_type: uploadData.media_type,
          category,
          url: uploadData.url,
          thumbnail_url: uploadData.media_type === 'photo' ? uploadData.url : null,
        }),
      });
      setProgress(90);

      if (mediaRes.ok) {
        const newMedia = await mediaRes.json();
        toast.success('Media uploaded successfully');
        setProgress(100);
        onUploaded(newMedia);
      } else {
        throw new Error('Failed to create media record');
      }
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    }
    setUploading(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="upload-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-sm font-bold flex items-center gap-2">
            <Upload className="w-4 h-4" style={{ color }} /> Upload Media
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* File input */}
          {!file ? (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-zinc-800 rounded-xl p-8 cursor-pointer hover:border-zinc-600 transition-colors" data-testid="file-dropzone">
              <Upload className="w-8 h-8 text-zinc-600 mb-2" />
              <span className="text-zinc-400 text-xs font-medium">Click to select a photo or video</span>
              <span className="text-zinc-600 text-[10px] mt-1">JPG, PNG, WebP, MP4, MOV — up to 500MB</span>
              <input type="file" className="hidden" accept="image/*,video/*" onChange={handleFile} data-testid="file-input" />
            </label>
          ) : (
            <div className="relative">
              {preview ? (
                <img src={preview} alt="" className="w-full aspect-video object-cover rounded-xl border border-zinc-800" />
              ) : (
                <div className="w-full aspect-video bg-zinc-900 border border-zinc-800 rounded-xl flex items-center justify-center">
                  <Video className="w-8 h-8 text-zinc-600" />
                  <span className="text-zinc-400 text-xs ml-2">{file.name}</span>
                </div>
              )}
              <button
                onClick={() => { setFile(null); setPreview(null); }}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Title */}
          <Input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="bg-zinc-900 border-zinc-800 text-white text-xs"
            data-testid="media-title-input"
          />

          {/* Team picker */}
          <select
            value={teamId}
            onChange={e => setTeamId(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-lg px-3 py-2 outline-none focus:border-zinc-600"
            data-testid="media-team-select"
          >
            <option value="">No team (program-wide)</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          {/* Event picker */}
          <select
            value={eventId}
            onChange={e => setEventId(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-lg px-3 py-2 outline-none focus:border-zinc-600"
            data-testid="media-event-select"
          >
            <option value="">No event</option>
            {events.map(e => <option key={e.id} value={e.id}>{e.title} — {formatDate(e.start_time)}</option>)}
          </select>

          {/* Category picker */}
          <div className="flex gap-2" data-testid="media-category-picker">
            {Object.entries(CATEGORY_META).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <button
                  key={key}
                  onClick={() => setCategory(key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                    category === key ? meta.cls + ' font-semibold' : 'bg-zinc-900 text-zinc-500 border-zinc-800 hover:border-zinc-700'
                  }`}
                  data-testid={`category-${key}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {meta.label}
                </button>
              );
            })}
          </div>

          {/* Upload progress */}
          {uploading && (
            <div className="space-y-1">
              <div className="w-full bg-zinc-800 rounded-full h-1.5">
                <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: color }} />
              </div>
              <p className="text-[10px] text-zinc-500 text-center">Uploading... {progress}%</p>
            </div>
          )}

          {/* Submit */}
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full text-xs font-semibold gap-2"
            style={{ backgroundColor: file && !uploading ? color : undefined }}
            data-testid="upload-submit-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : 'Upload Media'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProgramMedia;
